import { useState } from "react";
import type { StateMessage } from "../api/types";
import { InputForm } from "../components/InputForm";
import { InputTile } from "../components/InputTile";
import { OutputStatePill } from "../components/status";
import { Button, EmptyState, PageHeader, Panel, Pill, Stat } from "../components/ui";
import { useCan, useClientLimits } from "../lib/auth";
import { formatBitrate, formatBytes, formatNumber, outputProtocolLabel } from "../lib/format";
import { t } from "../lib/i18n";
import { useLive } from "../lib/live";
import { href, navigate } from "../lib/router";

export function Dashboard({ state }: { state: StateMessage }) {
  const { history } = useLive();
  const can = useCan();
  const isClient = useClientLimits() !== null;     // compte client : ses flux, pas l'état du serveur
  const [creating, setCreating] = useState(false);
  const { totals, inputs, outputs, alarms } = state;
  const c = totals.connections;
  const sys = state.system;
  // part de la gateway : API + MediaMTX + toutes les sorties FFmpeg
  const gatewayCpu = (sys?.api?.cpu_percent ?? 0) + (sys?.mediamtx?.cpu_percent ?? 0)
    + outputs.reduce((sum, o) => sum + (o.status.cpu_percent ?? 0), 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Tableau de bord")} subtitle={t("État des flux en temps réel")}
        actions={can("operator") && <Button variant="primary" onClick={() => setCreating(true)}>{t("Nouvelle entrée")}</Button>} />

      <div className={`grid grid-cols-2 gap-x-6 gap-y-4 rounded-lg border border-line bg-surface px-5 py-4 sm:grid-cols-3 lg:grid-cols-4 ${isClient ? "" : "xl:grid-cols-7"}`}>
        <Stat label={t("Entrées en ligne")} value={`${totals.inputs_online} / ${totals.inputs}`}
          tone={totals.inputs_online < inputs.filter((i) => i.enabled).length ? "warn" : "ok"} />
        <Stat label={t("Sorties en service")} value={`${totals.outputs_running} / ${totals.outputs}`}
          tone={outputs.some((o) => o.status.state === "retrying") ? "err" : undefined} />
        <Stat label={t("Débit entrant")} value={formatBitrate(totals.bitrate_in_kbps)} />
        <Stat label={t("Débit sortant")} value={formatBitrate(totals.bitrate_out_kbps)} />
        {!isClient && <>
        <Stat label={t("Connexions")} value={c.total}
          sub={`SRT ${c.srt} · RTMP ${c.rtmp} · RTSP ${c.rtsp} · WebRTC ${c.webrtc}`} />
        <Stat label={t("Processeur")} value={sys ? `${formatNumber(sys.cpu_percent)} %` : "–"}
          tone={sys && sys.cpu_percent >= 90 ? "err" : sys && sys.cpu_percent >= 70 ? "warn" : undefined}
          sub={sys ? t("gateway {cpu} % · {n} cœurs", { cpu: formatNumber(gatewayCpu), n: sys.cpu_count }) : undefined} />
        <Stat label={t("Mémoire")} value={sys ? `${Math.round((sys.memory_used_bytes / sys.memory_total_bytes) * 100)} %` : "–"}
          tone={sys && sys.memory_used_bytes / sys.memory_total_bytes >= 0.9 ? "warn" : undefined}
          sub={sys ? t("{used} sur {total}", { used: formatBytes(sys.memory_used_bytes), total: formatBytes(sys.memory_total_bytes) }) : undefined} />
        </>}
      </div>

      {/* Pas de liste des alarmes ici : elle apparaissait et disparaissait au-dessus des vignettes et
          décalait la page. Elles sont dans la page Journal (compteur dans le menu et dans la barre du haut),
          et chaque vignette montre l'état de son entrée. */}
      {inputs.length === 0 ? (
        <EmptyState title={t("Aucune entrée")}
          action={can("operator") && <Button variant="primary" onClick={() => setCreating(true)}>{t("Créer la première entrée")}</Button>}>
          {t("Une entrée reçoit un flux : un encodeur qui envoie en SRT ou RTMP, ou une caméra IP que la gateway va chercher.")}
        </EmptyState>
      ) : (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(100%,290px),1fr))]">
          {inputs.map((i) => <InputTile key={i.id} input={i} alarms={alarms} history={history[i.id] ?? []} />)}
        </div>
      )}

      {outputs.length > 0 && (
        <Panel title={t("Sorties")} actions={<a href={href({ page: "outputs" })} className="text-[13px] text-accent hover:underline">{t("Gérer")}</a>}>
          <ul className="divide-y divide-line">
            {outputs.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center gap-3 px-4 py-2">
                <OutputStatePill state={o.status.state} />
                <span className="min-w-0 flex-1 truncate">{o.name}</span>
                {o.status.on_backup && <Pill tone="warn">{t("Secours")}</Pill>}
                <span className="font-mono text-[12px] text-faint">
                  {o.status.active_input ?? o.input_id} → {outputProtocolLabel(o.protocol)}
                </span>
                <span className="num w-24 text-right">{formatBitrate(o.status.bitrate_kbps)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {creating && (
        <InputForm onClose={() => setCreating(false)} onSaved={(saved) => navigate({ page: "input", id: saved.id })} />
      )}
    </div>
  );
}
